import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import DebouncedAutoComplete from './DebouncedAutoComplete';
import {
  useFetchCountriesSuggestions,
  useGetCountryIsoCode,
  useGetCountryName,
} from '../../hooks/useFetchCountriesSuggestions';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

type RequiredFormikValues = {
  country?: string;
};

export type CountryInputProps<T extends RequiredFormikValues> = {
  formik: FormikProps<T>;
  label: string;
  validationMessage?: string;
  onChange: (value: string) => void;
  style?: CSSProperties;
};

export const CountryInput = <T extends RequiredFormikValues>({
  formik,
  label,
  validationMessage,
  onChange,
  style,
}: CountryInputProps<T>) => {
  const { t } = useTranslation();
  const { values, setFieldValue, errors } = formik as unknown as FormikProps<RequiredFormikValues>;
  const fetchCountrySuggestions = useFetchCountriesSuggestions();
  const getCountryIsoCode = useGetCountryIsoCode();
  const getCountryName = useGetCountryName();
  const onCountryChange = useCallback(
    async (value: string) => {
      const countryIso = await getCountryIsoCode(value);
      if (countryIso) {
        await setFieldValue('country', countryIso);
        onChange(countryIso);
      } else {
        onChange('default');
      }
    },
    [getCountryIsoCode, setFieldValue, onChange]
  );
  const [countryInputValue, setCountryInputValue] = useState('');
  useEffect(() => {
    if (values.country) {
      getCountryName(values.country).then((res) => setCountryInputValue(res || ''));
    }
  }, [values.country, getCountryName]);
  const validationMsg = errors.country || validationMessage;

  return (
    <DebouncedAutoComplete
      label={t(label)}
      value={countryInputValue}
      onChange={onCountryChange}
      fetchSuggestions={fetchCountrySuggestions}
      validationMessage={validationMsg ? t(validationMsg) : undefined}
      style={style}
      dataCy="CountryInput"
    />
  );
};
